'use client';

import { useId, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackSignupStarted } from '@/lib/utils/analytics';
import { cn } from '@/lib/utils';

interface EmailCaptureFormProps {
  className?: string;
  plan?: 'free' | 'starter' | 'pro';
  billingCycle?: 'monthly' | 'yearly';
  size?: 'default' | 'lg';
  /** Use `dark` on Umbra backgrounds. */
  tone?: 'light' | 'dark';
  /** Where the form sits on the page, added to the path for analytics. */
  placement?: string;
}

export function EmailCaptureForm({
  className,
  plan = 'starter',
  billingCycle = 'monthly',
  size = 'default',
  tone = 'light',
  placement,
}: EmailCaptureFormProps) {
  const [email, setEmail] = useState('');
  const inputId = useId();
  const { push } = useRouter();
  const pathname = usePathname();
  const dark = tone === 'dark';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const params = new URLSearchParams({
      redirect_url:
        plan === 'free'
          ? '/onboarding?plan=free'
          : `/onboarding?plan=${plan}&billing=${billingCycle}`,
      plan,
      billing: billingCycle,
    });

    if (email) {
      params.set('email', email);
    }

    trackSignupStarted(plan, placement ? `${pathname}:${placement}` : pathname);
    push(`/sign-up?${params.toString()}`);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex w-full flex-col items-center gap-2 sm:w-auto sm:flex-row',
        className
      )}
    >
      <label htmlFor={inputId} className="sr-only">
        Work email
      </label>
      <input
        id={inputId}
        name="email"
        type="email"
        autoComplete="email"
        inputMode="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Enter your work email"
        className={cn(
          'w-full rounded-full border px-5 text-sm transition-all outline-none focus:ring-2',
          dark
            ? 'focus:border-accent-50/70 focus:ring-accent-50/25 border-white/15 bg-white/[0.06] text-white backdrop-blur-sm placeholder:text-white/45 hover:border-white/25'
            : 'border-border text-foreground focus:border-ring focus:ring-ring bg-white placeholder:text-gray-400 focus:ring-inset',
          size === 'lg' ? 'py-3.5 sm:w-[300px]' : 'py-3 sm:w-[240px]'
        )}
      />
      <Button
        type="submit"
        variant={dark ? 'glow' : 'eclipse'}
        className={cn(
          'group w-full shrink-0 rounded-full sm:w-auto',
          size === 'lg'
            ? 'h-auto px-7 py-3.5 text-sm'
            : 'h-auto px-6 py-3 text-sm'
        )}
      >
        Start free trial
        <ArrowRight
          className="size-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </Button>
    </form>
  );
}
